/* eslint-disable */
// src/layouts/machine/index.js

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import AvailableMachineGroups from "layouts/machine/AvailableMachineGroups";

// Services & Forms
import machineService from "services/machineService";
import CreateMachineForm from "../../forms/machine/createMachineForm";
import UpdateMachineForm from "../../forms/machine/updateMachineForm";

import AppFormDialog from "components/common/AppFormDialog";
import Button from "@mui/material/Button";

function Machine() {
  const navigate = useNavigate();
  const location = useLocation();

  // Disponibles
  const [machinesDetails, setMachinesDetails] = useState([]);
  const [machines, setMachines] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Modales
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [machineToEdit, setMachineToEdit] = useState(null);

const loadAvailable = useCallback(async () => {
  setIsLoading(true);
  try {
    const machinesDetailsData = await machineService.getAvailableMachinesWithDetails();

    const machinesData = (machinesDetailsData || []).map((machineDetails) => ({
      Id: machineDetails.Id,
      Name: machineDetails.Name,
      SerialNumber: machineDetails.SerialNumber,
      CreationDate: machineDetails.CreationDate,
      MachineTypeId: machineDetails.MachineType?.Id || null,
      MachineType: machineDetails.MachineType || null,
    }));

    setMachinesDetails(machinesDetailsData || []);
    setMachines(machinesData);
  } catch (error) {
    console.error("Error al cargar los datos de máquinas:", error);
    setMachinesDetails([]);
    setMachines([]);
  } finally {
    setIsLoading(false);
  }
}, []);

useEffect(() => {
  loadAvailable();
}, [loadAvailable, location.key]);

const refreshMachines = loadAvailable;

useEffect(() => {
  const onFocus = () => {
    loadAvailable();
  };

  const onVisibility = () => {
    if (document.visibilityState === "visible") {
      loadAvailable();
    }
  };

  window.addEventListener("focus", onFocus);
  document.addEventListener("visibilitychange", onVisibility);

  return () => {
    window.removeEventListener("focus", onFocus);
    document.removeEventListener("visibilitychange", onVisibility);
  };
}, [loadAvailable]);

  // Modales
  const handleOpenCreateModal = () => setIsCreateModalOpen(true);
  const handleCloseCreateModal = () => setIsCreateModalOpen(false);

  const handleOpenEditModal = (machineId) => {
    const machine = machines.find((m) => m.Id === machineId);
    setMachineToEdit(machine);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setMachineToEdit(null);
  };

  const toMachineSimple = (md) => ({
  Id: md.Id,
  Name: md.Name,
  SerialNumber: md.SerialNumber,
  CreationDate: md.CreationDate, // o md.CreationDateTime según tu DTO
  MachineTypeId: md.MachineType?.Id || md.MachineTypeId || null,
  MachineType: md.MachineType || null,
});

// ====== TÍTULO DINÁMICO PARA EDITAR ======
  const editDialogTitle = useMemo(() => {
    const type = machineToEdit?.MachineType?.Type || machineToEdit?.MachineTypeLabel || "";
    const name = machineToEdit?.Name || machineToEdit?.Machine?.Name || "";
    const serial = machineToEdit?.SerialNumber || "";

    return ["Editar máquina", type, name, serial].filter(Boolean).join(" ");
  }, [machineToEdit]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={3} pb={3}>
        <Grid container spacing={6}>
          {/* ====== Disponibles ====== */}
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={1}
                flexWrap="wrap"
              >
                <MDTypography variant="h6" color="white">
                  Máquinas disponibles
                </MDTypography>
                <MDBox display="flex" gap={1} flexWrap="wrap">
                  <Button color="inherit" onClick={handleOpenCreateModal}>
                    Nueva máquina
                  </Button>
                  <Button color="inherit" onClick={() => navigate("/machine/inuse")}>
                    Ver en uso
                  </Button>
                  <Button color="inherit" onClick={() => navigate("/machine/eliminated")}>
                    Ver eliminadas
                  </Button>
                </MDBox>
              </MDBox>

              <MDBox pt={3}>
                <AvailableMachineGroups
                  machines={machinesDetails}
                  onEdit={handleOpenEditModal}
                  isLoading={isLoading}
                />
              </MDBox>

            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* Modales */}
      <AppFormDialog open={isCreateModalOpen} onClose={handleCloseCreateModal} title="Nueva máquina" maxWidth="md">
          <CreateMachineForm
            closeForm={handleCloseCreateModal}
            onCreateSuccess={async (newId) => {
              try {
                if (!newId) {
                  await refreshMachines();
                  return;
                }

                // 1) Traer la máquina real (DTO con details)
                const createdDetails = await machineService.getMachineByIdWithDetails(newId);

                // 2) Insertar/Upsert inmediato en la lista Available (sin refetch)
                if (createdDetails) {
                  setMachinesDetails((prev) => {
                    const idx = prev.findIndex((x) => x.Id === createdDetails.Id);
                    if (idx >= 0) {
                      const next = [...prev];
                      next[idx] = createdDetails;
                      return next;
                    }
                    return [createdDetails, ...prev];
                  });

                  setMachines((prev) => {
                    const simple = toMachineSimple(createdDetails);
                    const idx = prev.findIndex((x) => x.Id === simple.Id);
                    if (idx >= 0) {
                      const next = [...prev];
                      next[idx] = simple;
                      return next;
                    }
                    return [simple, ...prev];
                  });
                }
              } finally {
                // 3) “por seguridad”: refetch completo
                await refreshMachines();
              }
            }}
          />

      </AppFormDialog>

      {machineToEdit && (
        <AppFormDialog open={isEditModalOpen} onClose={handleCloseEditModal} title={editDialogTitle}>
            <UpdateMachineForm
              closeForm={handleCloseEditModal}
              machine={machineToEdit}
              onUpdateSuccess={async (updatedId) => {
                const id = updatedId ?? machineToEdit?.Id;

                try {
                  if (!id) return;

                  // 1) Traer la máquina actualizada con details
                  const updatedDetails = await machineService.getMachineByIdWithDetails(id);
                  if (!updatedDetails) return;

                  // Reemplazar inmediato en "Disponibles" (solo si existe ahí)
                  setMachinesDetails((prev) => {
                    const idx = prev.findIndex((x) => x.Id === id);
                    if (idx < 0) return prev;
                    const next = [...prev];
                    next[idx] = updatedDetails;
                    return next;
                  });

                  setMachines((prev) => {
                    const idx = prev.findIndex((x) => x.Id === id);
                    if (idx < 0) return prev;
                    const next = [...prev];
                    next[idx] = toMachineSimple(updatedDetails);
                    return next;
                  });

                } finally {
                  // “Por seguridad”: refetch completo
                  await refreshMachines();
                }
              }}
            />

        </AppFormDialog>
      )}

    </DashboardLayout>
  );
}

export default Machine;
